import React, { useState, useEffect } from 'react';
import { fetchAPI } from 'shared/api';

import LoginForm from './LoginForm';
import RestaurantProfile from './RestaurantProfile';
import MenuManagement from './MenuManagement';

// Helper to fetch restaurant for manager
async function fetchRestaurant(token) {
  try {
    const response = await fetchAPI('/restaurant/manager', {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch restaurant');
    const data = await response.json();
    if (Array.isArray(data) && data.length > 0) return data[0];
    return null;
  } catch (err) {
    return null;
  }
}

const baseTabs = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'restaurant', label: 'Restaurant Profile' },
  { key: 'orders', label: 'Orders' },
  { key: 'delivery', label: 'Delivery Staff' },
  { key: 'menu', label: 'Menu Management' },
  { key: 'documents', label: 'Document Upload' },
  { key: 'onboarding', label: 'Onboarding Status' },
  { key: 'settings', label: 'Settings' },
];

export default function MainLayout() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [restaurantLoading, setRestaurantLoading] = useState(false);

  // Tabs: show Login/Register if not logged in, else show user's name
  const tabs = [
    ...baseTabs,
    token
      ? { key: 'profile', label: user?.name || 'Profile' }
      : { key: 'auth', label: 'Login / Register' },
  ];

  // Handle logout
  const handleLogout = () => {
    setToken('');
    setUser(null);
    setActiveTab('dashboard');
  };

  // Handle tab click
  const handleTabClick = (key) => {
    if (key === 'profile') {
      setActiveTab('profile');
    } else {
      setActiveTab(key);
    }
  };


  // Handle login success (from LoginForm)
  const handleLogin = (tokenVal, userObj) => {
    console.log('Login successful:', tokenVal, userObj);
    setToken(tokenVal);
    setUser(userObj);
    setActiveTab('profile');
  };

  // Fetch restaurant immediately after login (when token changes)
  useEffect(() => {
    if (token) {
      setRestaurantLoading(true);
      fetchRestaurant(token).then(r => {
        setRestaurant(r);
        setRestaurantLoading(false);
      });
    } else {
      setRestaurant(null);
    }
  }, [token]);

  // Fetch restaurant when switching to restaurant/menu tab
  useEffect(() => {
    if (token && (activeTab === 'restaurant' || activeTab === 'menu')) {
      setRestaurantLoading(true);
      fetchRestaurant(token).then(r => {
        setRestaurant(r);
        setRestaurantLoading(false);
      });
    }
  }, [token, activeTab]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <aside style={{ width: 240, background: '#1f2937', color: 'white', padding: '20px 0' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 30, fontSize: 18 }}>Restaurant Manager</h2>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            style={{
              display: 'block',
              width: '100%',
              padding: '12px 24px',
              background: activeTab === tab.key ? '#374151' : 'transparent',
              color: 'white',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 'bold' : 'normal',
            }}
          >
            {tab.label}
          </button>
        ))}
      </aside>
      <main style={{ flex: 1, padding: 32, background: '#f9fafb' }}>
        {activeTab === 'dashboard' && (
          <div>
            <h1>Dashboard</h1>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {/* Restaurant Card */}
              <div style={{ background: 'white', borderRadius: 8, padding: 24, minWidth: 320, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1 }}>
                <h3 style={{ fontSize: 18, marginBottom: 12 }}>Restaurant</h3>
                {restaurant ? (
                  <>
                    <div><strong>Name:</strong> {restaurant.name || restaurant.restaurant_name}</div>
                    <div><strong>Contact:</strong> {restaurant.details?.contact_number || restaurant.contact_number}</div>
                    <div><strong>Pro Membership till:</strong> {restaurant.pro_membership_till || 'N/A'}</div>
                    <button style={{ marginTop: 12, background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }} onClick={() => setActiveTab('restaurant')}>View Profile</button>
                  </>
                ) : <div>No restaurant registered.</div>}
              </div>
              {/* Orders Card */}
              <div style={{ background: 'white', borderRadius: 8, padding: 24, minWidth: 320, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1 }}>
                <h3 style={{ fontSize: 18, marginBottom: 12 }}>Orders</h3>
                {/* Placeholder for order list */}
                <div>No orders found.</div>
                <button style={{ marginTop: 12, background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }} onClick={() => setActiveTab('orders')}>Go to Orders</button>
              </div>
              {/* Delivery Staff Card */}
              <div style={{ background: 'white', borderRadius: 8, padding: 24, minWidth: 320, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1 }}>
                <h3 style={{ fontSize: 18, marginBottom: 12 }}>Delivery Staff</h3>
                {/* Placeholder for delivery staff list */}
                <div>No delivery staff found.</div>
                <button style={{ marginTop: 12, background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }} onClick={() => setActiveTab('delivery')}>Go to Delivery Staff</button>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'orders' && (
          <div>
            <h1>Orders</h1>
            <div style={{ background: 'white', borderRadius: 6, padding: 24, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {/* TODO: Implement order list and status */}
              <p>Order list coming soon...</p>
            </div>
          </div>
        )}
        {activeTab === 'delivery' && (
          <div>
            <h1>Delivery Staff</h1>
            <div style={{ background: 'white', borderRadius: 6, padding: 24, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {/* TODO: Implement delivery staff list and load */}
              <p>Delivery staff list coming soon...</p>
            </div>
          </div>
        )}
        {activeTab === 'auth' && !token && (
          <LoginForm setToken={(tokenVal, userObj) => handleLogin(tokenVal, userObj)} />
        )}
        {activeTab === 'profile' && token && user && (
          <div>
            <h1>Profile</h1>
            <div style={{ background: 'white', borderRadius: 6, padding: 24, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 400 }}>
              <div style={{ marginBottom: 16 }}><strong>Name:</strong> {user.name}</div>
              <div style={{ marginBottom: 16 }}><strong>Email:</strong> {user.email}</div>
              <div style={{ marginBottom: 16 }}><strong>User Type:</strong> {user.user_type}</div>
              {user.details?.phone && (
                <div style={{ marginBottom: 16 }}><strong>Phone:</strong> {user.details.phone}</div>
              )}
              {user.details?.address && (
                <div style={{ marginBottom: 16 }}><strong>Address:</strong> {user.details.address}</div>
              )}
              <button onClick={handleLogout} style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', marginTop: 24 }}>Logout</button>
            </div>
          </div>
        )}
        {activeTab === 'restaurant' && token && (
          <RestaurantProfile token={token} restaurant={restaurant} refreshRestaurant={async () => {
            setRestaurantLoading(true);
            const rest = await fetchRestaurant(token);
            setRestaurant(rest);
            setRestaurantLoading(false);
          }} />
        )}
        {activeTab === 'menu' && token && (
          restaurantLoading ? <div>Loading restaurant...</div> :
          restaurant ? <MenuManagement token={token} restaurantId={restaurant.id || restaurant.restaurant_staging_id} />
          : <div>Please register your restaurant first.</div>
        )}
        {activeTab !== 'dashboard' && activeTab !== 'auth' && activeTab !== 'profile' && activeTab !== 'restaurant' && activeTab !== 'menu' && (
          <div>
            <h1>{tabs.find(t => t.key === activeTab)?.label}</h1>
            <div style={{ background: 'white', borderRadius: 6, padding: 24, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {/* TODO: Add content for {activeTab} */}
              <p>Coming soon...</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
